// MA-697 — `count_on_line`: the addition JUMP picture ("Start at 5. Jump
// forward 3. Where do you land?") for a stimulus-sourced, number_line-authored
// addition record — the addition counterpart of MA-655's `take_away_line`.
//
// WHY A NEW RENDERER (not `number_line`): the `number_line` renderer (MA-491,
// rendererRegistry.tsx) labels EVERY tick except the start and the landing,
// so the one unlabeled tick names the answer by elimination, and its dashed
// last hop ends on it. MA-94 §3b RED-1 therefore refuses it for a
// stimulus-sourced item (contentToEngineItem.ts `lineSafe`). Before MA-697
// nothing replaced it and the item fell to two static `groups` piles.
//
// THE PICTURE:
//   - a 0..10 line (0..20 when a+b > 10), one tick per unit, a longer tick
//     every 5 (structure only — every multiple of 5 gets one, landing or not);
//   - numerals ONLY at values <= a: 0, the multiples of 5 below a, and the
//     start `a` itself (bold, operand-A colour, on a filled start marker);
//   - `b` countable hop arcs from the start, operand-B colour; like MA-491 the
//     last hop is dashed and has no arrowhead, and nothing marks the landing
//     tick (no dot, no numeral). The child counts on along the hops.
//
// ANSWER-SAFETY BY CONSTRUCTION: this component reads only `a` and `b`. It
// never destructures or reads `total`, and no numeral greater than `a` is ever
// emitted (the label set is filtered to n <= a). Tick hooks carry only
// "major"/"minor", never a value, so the landing number is not in the markup.
// The range is one of two fixed lines chosen by whether a+b passes 10, so the
// line's end is not the answer
// (except a sum of exactly 10 or 20, whose landing is the unlabeled end tick —
// still counted to; the line-end/extent readability is an accepted operator
// decision, see docs/build/reports/MA-803-count-on-line-geometry-2026-10-03.md).
// Pinned against the real served pool in
// src/lib/engine/__tests__/countOnLine.test.mjs and addSubVisualIntegrityTest.
import { useId } from "react";
import type { MathRendererProps } from "./rendererRegistry";
import { OPERAND_A_FILL, OPERAND_B_FILL } from "./operandPalette";

/** The longest line this renderer draws (the mapper gates a+b on it). */
export const COUNT_ON_LINE_MAX = 20;

const LINE = "var(--color-border-secondary, #cdbfae)";
const LABEL = "var(--color-text-secondary, #7a6f60)";
// The start numeral is the one number the child must read — dark ink for
// contrast (the orange marker under it carries the operand-A colour).
const START_TEXT = "var(--color-text-primary, #3d3228)";

function safeInt(n: unknown, max: number): number {
  const v = Math.floor(Number.isFinite(n as number) ? (n as number) : 0);
  return Math.max(0, Math.min(max, v));
}

/** The numerals the line prints: 0, the multiples of 5 below the start, and
 *  the start itself — never a value above the start. On the 0..20 line a
 *  landmark closer than 3 units to the start is dropped so the two labels
 *  never overlap (a 10 beside an 11). Exported for the unit pin. */
export function countOnLineLabels(start: number, maxN: number = 10): number[] {
  const minGap = maxN > 10 ? 3 : 1;
  const out: number[] = [];
  for (let n = 0; n < start; n += 5) if (n === 0 || start - n >= minGap) out.push(n);
  out.push(start);
  return out;
}

export function CountOnLineRenderer({ a, b }: MathRendererProps) {
  const markerUid = useId();
  const markerId = `col-hop-${markerUid.replace(/[^a-zA-Z0-9]/g, "")}`;
  const start = safeInt(a, COUNT_ON_LINE_MAX);
  const hops = safeInt(b, COUNT_ON_LINE_MAX - start);
  const maxN = start + hops > 10 ? 20 : 10;
  const X0 = 20;
  const X1 = 340;
  const LY = 66; // the line's y
  const x = (v: number) => X0 + (v / maxN) * (X1 - X0);
  // MA-697 (RED S4): the arrowhead scales with one unit of the line, so on
  // the 0..20 line (16 units per hop) it stays well under half a hop and the
  // hops read as separate jumps, not a coil. 0..10: 10 x 9; 0..20: 6 x 5.4.
  const unit = (X1 - X0) / maxN;
  const AW = Math.min(10, Math.round(unit * 0.375 * 10) / 10);
  const AH = Math.round(AW * 0.9 * 10) / 10;
  const labels = countOnLineLabels(start, maxN);
  const ticks = Array.from({ length: maxN + 1 }, (_, n) => n);
  const label = `Number line. Start at ${start}. ${hops} hop${hops === 1 ? "" : "s"} forward.`;
  return (
    <svg viewBox="0 0 360 104" width="100%" style={{ maxWidth: 360 }} role="img" aria-label={label}
      data-count-on-line="true">
      <defs>
        <marker id={markerId} data-arrow-width={AW} markerUnits="userSpaceOnUse" markerWidth={AW} markerHeight={AH} refX={AW * 0.8} refY={AH / 2} orient="auto">
          <polygon points={`0,0 ${AW},${AH / 2} 0,${AH}`} fill={OPERAND_B_FILL} />
        </marker>
      </defs>
      <line x1={x(0)} y1={LY} x2={x(maxN)} y2={LY} stroke={LINE} strokeWidth="3.5" strokeLinecap="round" />
      {ticks.map((n) => {
        const long = n % 5 === 0;
        return (
          <line key={n} data-tick={long ? "major" : "minor"} x1={x(n)} y1={long ? LY - 12 : LY - 8} x2={x(n)} y2={long ? LY + 12 : LY + 8}
            stroke={LINE} strokeWidth={long ? 3 : 2.2} />
        );
      })}
      {/* Countable hops — one arc per unit step from the start; the last is
          dashed with no arrowhead, so the landing tick is never marked. */}
      {Array.from({ length: hops }).map((_, j) => {
        const fx = x(start + j);
        const tx = x(start + j + 1);
        const midX = (fx + tx) / 2;
        const isLast = j === hops - 1;
        return (
          <path
            key={j}
            data-hop={j + 1}
            d={`M${fx},${LY - 9} Q${midX},${LY - 46 + (j % 2) * 6} ${tx},${LY - 9}`}
            stroke={OPERAND_B_FILL}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={isLast ? "5 5" : undefined}
            markerEnd={isLast ? undefined : `url(#${markerId})`}
          />
        );
      })}
      {labels.map((n) => {
        const isStart = n === start;
        return (
          <text key={n} x={x(n)} y="99" textAnchor="middle" fontSize={isStart ? 22 : 15}
            fontWeight={isStart ? 700 : 400} fill={isStart ? START_TEXT : LABEL}>{n}</text>
        );
      })}
      {/* Start marker — the only dot on the line. */}
      <circle data-start-marker="true" cx={x(start)} cy={LY} r="8.5" fill={OPERAND_A_FILL} />
    </svg>
  );
}

export default CountOnLineRenderer;
